import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LogOut } from "lucide-react";

import { usePosterReducers } from "@/src_new/shared/redux/store/useGetData";
import { AvatarButton } from "@/src_new/shared/legacy/components/ui/AvatarButton";
import { MenuPanel } from "@/src_new/shared/legacy/components/ui/MenuPanel";
import { getInitials } from "@/src_new/shared/legacy/components/layout/Header/helpers/authDisplayName";
import type {
  HeaderAuthControlProps,
  MenuAction,
} from "@/src_new/shared/legacy/components/layout/Header/types/headerAuthControl.types";
import { App_Url_New } from "@/src_new/shared/common/static";
import CommonLogoutModal from "@/src_new/shared/components/CommonLogoutModal";
import OtpResumeLogin from "@/src_new/features/auth/components/otp/OtpResumeLogin";
import { closeModal, openModal } from "@/src_new/shared/redux/reducers/uiSlice";

export default function HeaderAuthControl({
  className,
  loginButtonClassName,
  extraActions = [],
}: HeaderAuthControlProps) {
  const dispatch = useDispatch();
  const {
    auth: { customerAuth },
  } = usePosterReducers();

  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuPanelRef = useRef<HTMLDivElement | null>(null);

  const displayName =
    `${customerAuth?.client?.first_name || ""} ${customerAuth?.client?.last_name || ""}`.trim();
  const initials = getInitials(displayName || "Account");

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (!menuOpen || !menuPanelRef.current) return;

    menuPanelRef.current.animate(
      [
        { opacity: 0, transform: "translateY(-8px) scale(0.96)" },
        { opacity: 1, transform: "translateY(0) scale(1)" },
      ],
      {
        duration: 180,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "forwards",
      }
    );
  }, [menuOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.LOGOUT_CONFIRM,
        title: "Logout?",
        className: "max-w-md",
        children: <CommonLogoutModal displayName={displayName} />,
      })
    );
  };

  const menuActions: MenuAction[] = [
    ...extraActions,
    {
      label: "Logout",
      icon: <LogOut className="h-4 w-4" />,
      onClick: handleLogout,
      className:
        "w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-theme-danger hover:bg-theme-danger-soft transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
    },
  ];

  const handleLoginClick = () => {
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.AUTH_RETAIL_LOGIN,
        className: "max-w-lg",
        children: (
          <OtpResumeLogin
            isOpen
            onClose={() => dispatch(closeModal())}
            showUserLoginForm
            validationType="auto"
            autoRedirect={false}
            renderInline
          />
        ),
      })
    );
  };

  if (!customerAuth?.access_token) {
    return (
      <div className={className}>
        <button
          type="button"
          onClick={handleLoginClick}
          className={
            loginButtonClassName ||
            "rounded-lg bg-theme-accent px-4 py-2 font-semibold text-theme-text transition-colors hover:bg-theme-accent-hover"
          }
        >
          Login
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${className ?? ""}`} ref={menuRef}>
      <AvatarButton
        initials={initials}
        menuOpen={menuOpen}
        onClick={() => setMenuOpen((prev) => !prev)}
      />

      {menuOpen ? (
        <MenuPanel panelRef={menuPanelRef} displayName={displayName} actions={menuActions} />
      ) : null}
    </div>
  );
}
